import type { Transaction } from './model';
import { spendingOf } from './spending';

/**
 * Who is behind a household's spending (Phase 19). Every entry carries three
 * different people, and they answer three different questions:
 *
 *  - added by   who typed it in (bookkeeping; never changes)
 *  - paid by    whose money went out
 *  - belongs to the household (shared) or one person (personal: their own
 *               haircut, paid from the shared card or by someone else)
 *
 * Amounts follow the one spending rule (spendingOf): refunds count
 * negative, transfers and tracked card bills not at all.
 */
export type OwnershipFilter = 'shared' | { memberId: string };

export interface PersonTotals {
  memberId: string;
  /** Spending this person paid for (theirs, shared, or someone else's). */
  paid: number;
  /** Spending that is this person's own (personal entries they own). */
  own: number;
  /** Entries this person typed in. */
  added: number;
}

export interface HouseholdSummary {
  total: number;
  /** Spending that belongs to the whole household. */
  shared: number;
  /** One row per person, in the order given, then anyone else the entries mention (e.g. someone who left). */
  people: PersonTotals[];
}

type Entry = Pick<Transaction, 'type' | 'countsAsSpending' | 'amountMinor' | 'addedBy' | 'paidBy' | 'ownership' | 'ownerMemberId'>;

export function householdSummary(entries: readonly Entry[], memberIds: readonly string[]): HouseholdSummary {
  const people = new Map<string, PersonTotals>();
  const person = (id: string) => {
    let p = people.get(id);
    if (!p) people.set(id, (p = { memberId: id, paid: 0, own: 0, added: 0 }));
    return p;
  };
  memberIds.forEach(person);
  let total = 0;
  let shared = 0;
  for (const e of entries) {
    const amount = spendingOf(e);
    total += amount;
    person(e.addedBy).added += 1;
    person(e.paidBy).paid += amount;
    if (e.ownership === 'personal' && e.ownerMemberId) person(e.ownerMemberId).own += amount;
    else shared += amount;
  }
  return { total, shared, people: [...people.values()] };
}

export function matchesOwnership(e: Pick<Transaction, 'ownership' | 'ownerMemberId'>, filter: OwnershipFilter): boolean {
  return filter === 'shared' ? e.ownership === 'shared' : e.ownership === 'personal' && e.ownerMemberId === filter.memberId;
}

/** "shared" or a member id, as kept in a URL. */
export function parseOwnership(value: string | null | undefined): OwnershipFilter | undefined {
  if (!value) return undefined;
  return value === 'shared' ? 'shared' : { memberId: value };
}

export const ownershipKey = (f: OwnershipFilter) => (f === 'shared' ? 'shared' : f.memberId);

/* ------------------------------------------------------------------ settling up (V1.5) */

export interface Balance {
  memberId: string;
  /** Spending this person paid. */
  paid: number;
  /** What was theirs to bear: an equal part of the shared costs plus their own. */
  share: number;
  /** paid − share: above 0 they get money back, below 0 they owe. */
  balance: number;
}

export interface Transfer {
  from: string;
  to: string;
  amount: number;
}

type SettleEntry = Entry & { date?: string };

/**
 * Who owes whom. Shared costs are split equally between `memberIds` (the
 * people in the budget now; a cent that does not divide goes to the first
 * ones, so the parts always add up); someone's own cost is theirs, whoever
 * paid it. Balances then add up to 0, and the fewest transfers that settle
 * them are listed (biggest debt to biggest credit first; at most one fewer
 * than there are people).
 */
export function settleUp(entries: readonly SettleEntry[], memberIds: readonly string[]): { balances: Balance[]; transfers: Transfer[]; shared: number } {
  const rows = new Map<string, Balance>();
  const row = (id: string) => {
    let r = rows.get(id);
    if (!r) rows.set(id, (r = { memberId: id, paid: 0, share: 0, balance: 0 }));
    return r;
  };
  memberIds.forEach(row);
  let shared = 0;
  for (const e of entries) {
    const v = spendingOf(e);
    if (v === 0) continue;
    row(e.paidBy).paid += v;
    if (e.ownership === 'personal' && e.ownerMemberId) row(e.ownerMemberId).share += v;
    else shared += v;
  }
  if (memberIds.length > 0) {
    const n = memberIds.length;
    const part = Math.floor(shared / n);
    let rest = shared - part * n;
    for (const id of memberIds) {
      row(id).share += part + (rest > 0 ? 1 : 0);
      if (rest > 0) rest--;
    }
  }
  const balances = [...rows.values()].map((r) => ({ ...r, balance: r.paid - r.share }));

  const debtors = balances.filter((b) => b.balance < 0).map((b) => ({ id: b.memberId, left: -b.balance }));
  const creditors = balances.filter((b) => b.balance > 0).map((b) => ({ id: b.memberId, left: b.balance }));
  const transfers: Transfer[] = [];
  const byLeft = (a: { left: number; id: string }, b: { left: number; id: string }) => b.left - a.left || a.id.localeCompare(b.id);
  while (debtors.some((d) => d.left > 0) && creditors.some((c) => c.left > 0)) {
    debtors.sort(byLeft);
    creditors.sort(byLeft);
    const d = debtors[0]!;
    const c = creditors[0]!;
    const amount = Math.min(d.left, c.left);
    transfers.push({ from: d.id, to: c.id, amount });
    d.left -= amount;
    c.left -= amount;
  }
  return { balances, transfers, shared };
}

/** Who paid for each category: category → person → spending they paid (categories by total, biggest first). */
export function paidByCategory(entries: readonly (Entry & Pick<Transaction, 'categoryId'>)[]): { categoryId: string; total: number; byPerson: Map<string, number> }[] {
  const out = new Map<string, { categoryId: string; total: number; byPerson: Map<string, number> }>();
  for (const e of entries) {
    const v = spendingOf(e);
    if (v === 0) continue;
    let c = out.get(e.categoryId);
    if (!c) out.set(e.categoryId, (c = { categoryId: e.categoryId, total: 0, byPerson: new Map() }));
    c.total += v;
    c.byPerson.set(e.paidBy, (c.byPerson.get(e.paidBy) ?? 0) + v);
  }
  return [...out.values()].filter((c) => c.total > 0).sort((a, b) => b.total - a.total || a.categoryId.localeCompare(b.categoryId));
}
